/* eslint-disable max-lines */
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  Activity,
  ActivityTypeEnum,
  CommentableType,
  Comment,
  FormStatus,
  ObservationStatus,
  TaskStatus,
  ProjectFileStatus,
} from '@types'

import { TimeLine, TimeLineItem } from '@components/TimeLine'
import { UserBadge } from '@components/UserBadge'
import { SendProps, WriteComment } from '@components/WriteComment'

import FormDoneIcon from '@assets/images/form-status-done-icon.svg?react'
import FormInProgressIcon from '@assets/images/form-status-in-progress-icon.svg?react'
import FormToDoIcon from '@assets/images/form-status-todo-icon.svg?react'

import TaskDoneIcon from '@assets/images/task-status-done-icon.svg?react'
import TaskToDoIcon from '@assets/images/task-status-to-do-icon.svg?react'
import TaskWarningIcon from '@assets/images/task-status-warning-icon.svg?react'
import TaskInProgressIcon from '@assets/images/task-status-in-progress-icon.svg?react'

import ObservationDoneIcon from '@assets/images/observation-status-done-icon.svg?react'
import ObservationRecoveryIcon from '@assets/images/observation-status-recovery-icon.svg?react'
import ObservationContestedIcon from '@assets/images/observation-status-contested-icon.svg?react'
import ObservationToDoIcon from '@assets/images/observation-status-todo-icon.svg?react'

import ProjectFileRefusedIcon from '@assets/images/validation-refused-icon.svg?react'
import ProjectFilePendingIcon from '@assets/images/validation-pending-icon.svg?react'
import ProjectFileValidatedIcon from '@assets/images/validation-validated-icon.svg?react'
// eslint-disable-next-line max-len
import ProjectFileValidatedWithObservationIcon from '@assets/images/validation-validated-with-observation-icon.svg?react'

import { firstLetterUppercase } from '@utils/string'

import { CommentRow } from '../CommentRow'

import {
  ActionText,
  ActivityWrapper,
  Bullet,
  CommentWrapper,
  FormStatusIcon,
  IconWrapper,
  ObservationStatusIcon,
  ProjectFileAvailableIcon,
  ProjectStatusIcon,
  TaskStatusIcon,
  TextContent,
  TimelineContainer,
  WriteCommentContainer,
} from './TimelineActivities.styled'

type TimelineActivitiesProps = {
  activities: Activity[]
  showCommentActivities?: boolean
  type: CommentableType
  comments: Comment[]
  readOnlyComments?: boolean
  onSendComment?: (sendProps: SendProps) => void
  excludeActivities?: ActivityTypeEnum[]
}

export const TimelineActivities: React.FC<TimelineActivitiesProps> = ({
  activities,
  comments,
  showCommentActivities = false,
  excludeActivities = [],
  onSendComment,
  readOnlyComments = false,
}) => {
  const { t } = useTranslation()

  const commentActivities: ActivityTypeEnum[] = [
    ActivityTypeEnum.ActivityFormCommentAdd,
    ActivityTypeEnum.ActivityFormCommentDelete,
    ActivityTypeEnum.ActivityTaskCommentAdd,
    ActivityTypeEnum.ActivityTaskCommentDelete,
    ActivityTypeEnum.ActivityObservationCommentAdd,
    ActivityTypeEnum.ActivityObservationCommentDelete,
  ]

  const activityLabels: Partial<Record<ActivityTypeEnum, string>> = {
    [ActivityTypeEnum.ActivityFormCreate]: t('modules_shared_components_TimelineActivities_label_activity_form_create'),
    [ActivityTypeEnum.ActivityFormUpdate]: t('modules_shared_components_TimelineActivities_label_activity_form_update'),
    [ActivityTypeEnum.ActivityFormStatusChange]: t(
      'modules_shared_components_TimelineActivities_label_activity_form_status_change',
    ),
    [ActivityTypeEnum.ActivityFormCommentAdd]: t(
      'modules_shared_components_TimelineActivities_label_activity_form_comment_add',
    ),
    [ActivityTypeEnum.ActivityFormCommentDelete]: t(
      'modules_shared_components_TimelineActivities_label_activity_form_comment_delete',
    ),
    [ActivityTypeEnum.ActivityObservationCommentAdd]: t(
      'modules_shared_components_TimelineActivities_label_activity_observation_comment_add',
    ),
    [ActivityTypeEnum.ActivityObservationCommentDelete]: t(
      'modules_shared_components_TimelineActivities_label_activity_observation_comment_delete',
    ),
    [ActivityTypeEnum.ActivityObservationCreate]: t(
      'modules_shared_components_TimelineActivities_label_activity_observation_create',
    ),
    [ActivityTypeEnum.ActivityObservationFileAdd]: t(
      'modules_shared_components_TimelineActivities_label_activity_observation_file_add',
    ),
    [ActivityTypeEnum.ActivityObservationStatusChange]: t(
      'modules_shared_components_TimelineActivities_label_activity_observation_status_change',
    ),

    [ActivityTypeEnum.ActivityProjectFileCommentAdd]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_comment_add',
    ),
    [ActivityTypeEnum.ActivityProjectFileCommentDelete]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_comment_delete',
    ),
    [ActivityTypeEnum.ActivityProjectFileCreate]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_create',
    ),
    [ActivityTypeEnum.ActivityProjectFileStatusChange]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_status_change',
    ),
    [ActivityTypeEnum.ActivityProjectFileUpdate]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_update',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestCreate]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_create',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestCancel]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_cancel',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestAvailable]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_available',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestValidated]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_validated',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestValidatedWithObservation]: t(
      // eslint-disable-next-line max-len
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_validated_with_observation',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestRefused]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_refused',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestValidatedByUser]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_validated_by_user',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestValidatedWithObservationByUser]: t(
      // eslint-disable-next-line max-len
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_validated_with_observation_by_user',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestRefusedByUser]: t(
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_refused_by_user',
    ),
    [ActivityTypeEnum.ActivityProjectFileValidationRequestNotConcernedByUser]: t(
      // eslint-disable-next-line max-len
      'modules_shared_components_TimelineActivities_label_activity_project_file_validation_request_not_concerned_by_user',
    ),

    [ActivityTypeEnum.ActivityTaskCommentAdd]: t(
      'modules_shared_components_TimelineActivities_label_activity_task_comment_add',
    ),
    [ActivityTypeEnum.ActivityTaskCommentDelete]: t(
      'modules_shared_components_TimelineActivities_label_activity_task_comment_delete',
    ),
    [ActivityTypeEnum.ActivityTaskCreate]: t('modules_shared_components_TimelineActivities_label_activity_task_create'),
    [ActivityTypeEnum.ActivityTaskFileAdd]: t(
      'modules_shared_components_TimelineActivities_label_activity_task_file_add',
    ),
    [ActivityTypeEnum.ActivityTaskStatusChange]: t(
      'modules_shared_components_TimelineActivities_label_activity_task_status_change',
    ),
    [ActivityTypeEnum.ActivityTaskUpdate]: t('modules_shared_components_TimelineActivities_label_activity_task_update'),
  }

  const statusConfig: {
    form: {
      labels: Record<FormStatus, string>
      icons: Record<FormStatus, React.ReactNode>
    }
    task: {
      labels: Record<TaskStatus, string>
      icons: Record<TaskStatus, React.ReactNode>
    }
    observation: {
      labels: Record<ObservationStatus, string>
      icons: Record<ObservationStatus, React.ReactNode>
    }
    projectFile: {
      labels: Record<ProjectFileStatus | 'AVAILABLE', string>
      icons: Record<ProjectFileStatus | 'AVAILABLE', React.ReactNode>
    }
  } = {
    form: {
      labels: {
        [FormStatus.StatusToDo]: t('enum_FormStatus_to_do_label'),
        [FormStatus.StatusInProgress]: t('enum_FormStatus_in_progress_label'),
        [FormStatus.StatusDone]: t('enum_FormStatus_done_label'),
      },
      icons: {
        [FormStatus.StatusToDo]: <FormToDoIcon />,
        [FormStatus.StatusInProgress]: <FormInProgressIcon />,
        [FormStatus.StatusDone]: <FormDoneIcon />,
      },
    },
    task: {
      labels: {
        [TaskStatus.StatusToDo]: t('enum_TaskStatus_to_do_label'),
        [TaskStatus.StatusWarning]: t('enum_TaskStatus_warning_label'),
        [TaskStatus.StatusInProgress]: t('enum_TaskStatus_in_progress_label'),
        [TaskStatus.StatusDone]: t('enum_TaskStatus_done_label'),
      },
      icons: {
        [TaskStatus.StatusDone]: <TaskDoneIcon />,
        [TaskStatus.StatusWarning]: <TaskWarningIcon />,
        [TaskStatus.StatusInProgress]: <TaskInProgressIcon />,
        [TaskStatus.StatusToDo]: <TaskToDoIcon />,
      },
    },
    observation: {
      labels: {
        [ObservationStatus.StatusToDo]: t('enum_ObservationStatus_to_do_label'),
        [ObservationStatus.StatusPlannedRecovery]: t('enum_ObservationStatus_planned_recovery'),
        [ObservationStatus.StatusContested]: t('enum_ObservationStatus_contested'),
        [ObservationStatus.StatusValidatedByReader]: t('enum_ObservationStatus_validated_by_reader'),
        [ObservationStatus.StatusValidatedByCustomer]: t('enum_ObservationStatus_validated_by_customer'),
        [ObservationStatus.StatusValidatedByAdmin]: t('enum_ObservationStatus_validated_by_admin'),
      },
      icons: {
        [ObservationStatus.StatusToDo]: <ObservationToDoIcon />,
        [ObservationStatus.StatusPlannedRecovery]: <ObservationRecoveryIcon />,
        [ObservationStatus.StatusContested]: <ObservationContestedIcon />,
        [ObservationStatus.StatusValidatedByReader]: <ObservationDoneIcon />,
        [ObservationStatus.StatusValidatedByCustomer]: <ObservationDoneIcon />,
        [ObservationStatus.StatusValidatedByAdmin]: <ObservationDoneIcon />,
      },
    },
    projectFile: {
      labels: {
        AVAILABLE: t('enum_ProjectFileStatus_available'),
        [ProjectFileStatus.Pending]: t('enum_ProjectFileStatus_pending'),
        [ProjectFileStatus.Refused]: t('enum_ProjectFileStatus_refused'),
        [ProjectFileStatus.Validated]: t('enum_ProjectFileStatus_validated'),
        [ProjectFileStatus.ValidatedWithObservation]: t('enum_ProjectFileStatus_validated_with_observation'),
      },
      icons: {
        AVAILABLE: <ProjectFileAvailableIcon />,
        [ProjectFileStatus.Pending]: <ProjectFilePendingIcon />,
        [ProjectFileStatus.Refused]: <ProjectFileRefusedIcon />,
        [ProjectFileStatus.Validated]: <ProjectFileValidatedIcon />,
        [ProjectFileStatus.ValidatedWithObservation]: <ProjectFileValidatedWithObservationIcon />,
      },
    },
  }

  const excludedActivityTypesForUserBadges = [
    ActivityTypeEnum.ActivityProjectFileStatusChange,
    ActivityTypeEnum.ActivityProjectFileValidationRequestCreate,
  ]

  const getActivityTimelineItem = (activity: Activity): TimeLineItem & { timestamp: number } => ({
    DotComponent: () => (
      <IconWrapper>
        {activity.status && activity.type === ActivityTypeEnum.ActivityFormStatusChange && (
          <FormStatusIcon status={activity.status.toUpperCase() as FormStatus}>
            {statusConfig.form.icons[activity.status.toUpperCase() as FormStatus]}
          </FormStatusIcon>
        )}
        {activity.status && activity.type === ActivityTypeEnum.ActivityTaskStatusChange && (
          <TaskStatusIcon status={activity.status.toUpperCase() as TaskStatus}>
            {statusConfig.task.icons[activity.status.toUpperCase() as TaskStatus]}
          </TaskStatusIcon>
        )}
        {activity.status && activity.type === ActivityTypeEnum.ActivityObservationStatusChange && (
          <ObservationStatusIcon status={activity.status.toUpperCase() as ObservationStatus}>
            {statusConfig.observation.icons[activity.status.toUpperCase() as ObservationStatus]}
          </ObservationStatusIcon>
        )}
        {activity.type === ActivityTypeEnum.ActivityProjectFileStatusChange && (
          <ProjectStatusIcon
            status={activity.status ? (activity.status.toUpperCase() as ProjectFileStatus) : 'AVAILABLE'}
          >
            {
              statusConfig.projectFile.icons[
                activity.status ? (activity.status.toUpperCase() as ProjectFileStatus) : 'AVAILABLE'
              ]
            }
          </ProjectStatusIcon>
        )}
        {activity.type === ActivityTypeEnum.ActivityProjectFileValidationRequestCreate && (
          <ProjectStatusIcon status={ProjectFileStatus.Pending}>
            {statusConfig.projectFile.icons[ProjectFileStatus.Pending]}
          </ProjectStatusIcon>
        )}
        {activity.type === ActivityTypeEnum.ActivityProjectFileValidationRequestValidated && (
          <ProjectStatusIcon status={ProjectFileStatus.Validated}>
            {statusConfig.projectFile.icons[ProjectFileStatus.Validated]}
          </ProjectStatusIcon>
        )}
        {activity.type === ActivityTypeEnum.ActivityProjectFileValidationRequestValidatedWithObservation && (
          <ProjectStatusIcon status={ProjectFileStatus.ValidatedWithObservation}>
            {statusConfig.projectFile.icons[ProjectFileStatus.ValidatedWithObservation]}
          </ProjectStatusIcon>
        )}
        {activity.type === ActivityTypeEnum.ActivityProjectFileValidationRequestRefused && (
          <ProjectStatusIcon status={ProjectFileStatus.Refused}>
            {statusConfig.projectFile.icons[ProjectFileStatus.Refused]}
          </ProjectStatusIcon>
        )}
        {activity.type === ActivityTypeEnum.ActivityProjectFileValidationRequestAvailable && (
          <ProjectStatusIcon status="AVAILABLE">{statusConfig.projectFile.icons['AVAILABLE']}</ProjectStatusIcon>
        )}
        {!activity.status && excludedActivityTypesForUserBadges.indexOf(activity.type) == -1 && activity.user && (
          <UserBadge user={activity.user} size="small" />
        )}
      </IconWrapper>
    ),
    LabelComponent: () => (
      <ActivityWrapper>
        <TextContent>
          {activity.user && (
            <ActionText bold>
              {firstLetterUppercase(activity.user.firstName)} {firstLetterUppercase(activity.user.lastName)}
            </ActionText>
          )}
          <ActionText>{activityLabels[activity.type]}</ActionText>
          {activity.status && activity.type === ActivityTypeEnum.ActivityFormStatusChange && (
            <ActionText bold>{statusConfig.form.labels[activity.status.toUpperCase() as FormStatus]}</ActionText>
          )}
          {activity.status && activity.type === ActivityTypeEnum.ActivityTaskStatusChange && (
            <ActionText bold>{statusConfig.task.labels[activity.status.toUpperCase() as TaskStatus]}</ActionText>
          )}
          {activity.status && activity.type === ActivityTypeEnum.ActivityObservationStatusChange && (
            <ActionText bold>
              {statusConfig.observation.labels[activity.status.toUpperCase() as ObservationStatus]}
            </ActionText>
          )}
          {activity.type === ActivityTypeEnum.ActivityProjectFileStatusChange && (
            <ActionText bold>
              {
                statusConfig.projectFile.labels[
                  activity.status ? (activity.status.toUpperCase() as ProjectFileStatus) : 'AVAILABLE'
                ]
              }
            </ActionText>
          )}
          {activity.type === ActivityTypeEnum.ActivityProjectFileValidationRequestCreate &&
            activity.data?.validationUsers &&
            (activity.data?.validationUsers as { id: string; fullName: string }[]).map(
              (validationUser, index, data) => {
                return (
                  <React.Fragment key={validationUser.id}>
                    {index != 0 && index < data.length - 1 && <ActionText>,</ActionText>}
                    {index != 0 && index == data.length - 1 && <ActionText>et</ActionText>}
                    <ActionText bold>{validationUser.fullName}</ActionText>
                  </React.Fragment>
                )
              },
            )}
          <Bullet />
          <ActionText>{moment(activity.createdDate).fromNow()}</ActionText>
        </TextContent>
      </ActivityWrapper>
    ),
    timestamp: moment(activity.createdDate).toDate().getTime(),
  })

  const getCommentTimelineItem = (comment: Comment): TimeLineItem & { timestamp: number } => ({
    LabelComponent: () => (
      <CommentWrapper>
        <CommentRow readOnly={readOnlyComments} comment={comment} />
      </CommentWrapper>
    ),
    timestamp: moment(comment.createdDate).add(1, 'milliseconds').toDate().getTime(),
  })

  const mergedItems: TimeLineItem[] = [
    ...activities
      .filter(
        (item) =>
          (showCommentActivities ? true : !commentActivities.includes(item.type)) &&
          !excludeActivities.includes(item.type),
      )
      .map(getActivityTimelineItem),
    ...comments.map(getCommentTimelineItem),
  ].sort((left, right) => left.timestamp - right.timestamp)

  return (
    <TimelineContainer>
      <TimeLine items={mergedItems} />
      {onSendComment && (
        <WriteCommentContainer>
          <WriteComment onClickSend={onSendComment} userBadgeSize="small" />
        </WriteCommentContainer>
      )}
    </TimelineContainer>
  )
}
